import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MessageIcon from '@mui/icons-material/Message';
import BuildIcon from '@mui/icons-material/Build';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import StarIcon from '@mui/icons-material/Star';
import AdminLayout from '../../../components/AdminLayout';
import axios from 'axios';

const backendUrl =
    import.meta.env.MODE === 'development'
        ? `http://${import.meta.env.VITE_BACKEND_URL}`
        : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

const AdminDashboardPage = () => {
    const [data, setData] = useState();

    // Fetch dashboard data from API (uncomment when backend is ready)

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                console.log('Fetching dashboard data');
                const response = await axios.get(
                    `${backendUrl}/admin/dashboard`
                );
                setData(response.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            }
        };

        fetchDashboardData();
    }, []);

    return (
        <AdminLayout>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Admin Dashboard
                </Typography>

                <Grid container spacing={3}>
                    {/* Summary Cards */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <CardContent
                                sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <BuildIcon
                                    color="primary"
                                    sx={{ fontSize: 48, mb: 1 }}
                                />
                                <Typography variant="h5" component="div">
                                    {data?.totalServices}
                                </Typography>
                                <Typography variant="body2" color="primary">
                                    Services
                                </Typography>
                            </CardContent>
                            <Button
                                component={RouterLink}
                                to="/admin/services"
                                color="primary"
                                sx={{ mt: 'auto', mx: 2, mb: 2 }}
                            >
                                Manage Services
                            </Button>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <CardContent
                                sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <PhotoLibraryIcon
                                    color="primary"
                                    sx={{ fontSize: 48, mb: 1 }}
                                />
                                <Typography variant="h5" component="div">
                                    {data?.totalProjects}
                                </Typography>
                                <Typography variant="body2" color="primary">
                                    Projects
                                </Typography>
                            </CardContent>
                            <Button
                                component={RouterLink}
                                to="/admin/projects"
                                color="primary"
                                sx={{ mt: 'auto', mx: 2, mb: 2 }}
                            >
                                Manage Projects
                            </Button>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <CardContent
                                sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <StarIcon
                                    color="primary"
                                    sx={{ fontSize: 48, mb: 1 }}
                                />
                                <Typography variant="h5" component="div">
                                    {data?.totalTestimonials}
                                </Typography>
                                <Typography variant="body2" color="primary">
                                    Testimonials
                                </Typography>
                            </CardContent>
                            <Button
                                component={RouterLink}
                                to="/admin/testimonials"
                                color="primary"
                                sx={{ mt: 'auto', mx: 2, mb: 2 }}
                            >
                                Manage Testimonials
                            </Button>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <CardContent
                                sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <MessageIcon
                                    color="primary"
                                    sx={{ fontSize: 48, mb: 1 }}
                                />
                                <Typography variant="h5" component="div">
                                    {data?.unreadMessages}
                                </Typography>
                                <Typography variant="body2" color="primary">
                                    Unread Messages
                                </Typography>
                            </CardContent>
                            <Button
                                component={RouterLink}
                                to="/admin/messages"
                                color="primary"
                                sx={{ mt: 'auto', mx: 2, mb: 2 }}
                            >
                                View Messages
                            </Button>
                        </Card>
                    </Grid>

                    {/* Recent Messages */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6" component="h2">
                                    Recent Messages
                                </Typography>
                                <Button
                                    component={RouterLink}
                                    to="/admin/messages"
                                    color="primary"
                                >
                                    View All
                                </Button>
                            </Box>

                            <List>
                                {data?.recentMessages?.map((message, index) => (
                                    <React.Fragment key={message.id}>
                                        <ListItem
                                            alignItems="flex-start"
                                            sx={{
                                                backgroundColor: message.read
                                                    ? 'transparent'
                                                    : 'rgba(25, 118, 210, 0.05)',
                                                borderRadius: 1,
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                        }}
                                                    >
                                                        <Typography
                                                            component="span"
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight:
                                                                    message.read
                                                                        ? 400
                                                                        : 600,
                                                            }}
                                                        >
                                                            {message.name}
                                                        </Typography>
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            color="primary"
                                                        >
                                                            {message.date}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <>
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            color="primary"
                                                        >
                                                            {message.email}
                                                        </Typography>
                                                        {!message.read && (
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    display:
                                                                        'inline-block',
                                                                    ml: 1,
                                                                    px: 1,
                                                                    py: 0.25,
                                                                    bgcolor:
                                                                        'primary.main',
                                                                    color: 'white',
                                                                    borderRadius: 1,
                                                                    fontSize:
                                                                        '0.75rem',
                                                                }}
                                                            >
                                                                New
                                                            </Box>
                                                        )}
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                        {index <
                                            data?.recentMessages?.length -
                                                1 && <Divider component="li" />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </AdminLayout>
    );
};

export default AdminDashboardPage;
